import { boolean, object, string } from "yup";
import { dayjs } from "@thalamos/common";
import { defaultContextFields, generateDateBeforeNowValidationRule, nonEmptyString, placeOfSafety, } from "../shared.js";
export const placeOfSafetyTypeOptions = [
    {
        value: "health-based",
        label: "Health Based Place of Safety (S136 Suite)",
    },
    {
        value: "emergency-department",
        label: "Emergency Department (A&E)",
    },
    {
        value: "police-station",
        label: "Police station and it is intended for the assessment to take place at the police station",
    },
    {
        value: "private-dwelling",
        label: "Private Dwelling",
    },
];
export const intoxicationTypeOptions = [
    { value: "drugs", label: "Drugs" },
    { value: "alcohol", label: "Alcohol" },
    { value: "both", label: "Both" },
];
export const s136ExtensionFormPart1DataSchema = object({
    ...defaultContextFields({}),
    // Practitioner confirmation
    isRegisteredMedicalPractitioner: boolean()
        .default(null)
        .required("Please confirm you are the registered medical practitioner responsible for the examination")
        .oneOf([true], "You must be the registered medical practitioner responsible for this examination"),
    // Place of Safety - object containing name, address, postalCode, isConfirmed, and optional curated IDs
    placeOfSafety: placeOfSafety.required("Please select a Place of Safety"),
    // Place of Safety type
    placeOfSafetyTypeValue: nonEmptyString
        .required("Please select the Place of Safety type")
        .nonNullable("Please select the Place of Safety type"),
    placeOfSafetyTypeText: string().default(""),
    // Why extension is needed - multi-select (at least one required)
    reasonMentalHealth: boolean().default(false),
    reasonPhysicalHealth: boolean().default(false),
    reasonIntoxication: boolean().default(false),
    // Intoxication type (conditional)
    intoxicationTypeValue: string()
        .nullable()
        .default(null)
        .when("reasonIntoxication", {
        is: true,
        then: (schema) => schema.required("Please specify whether intoxication is due to drugs, alcohol, or both"),
        otherwise: (schema) => schema.notRequired(),
    }),
    intoxicationTypeText: string().default(""),
    // Full reason text (optional)
    fullReasonText: nonEmptyString.optional(),
    // Start date and time of detention
    detentionStartDateTime: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.").required("Please enter the date and time of detention"),
    // Extension hours (1-12) - stored as string value for dropdown compatibility
    extensionHoursValue: string()
        .nullable()
        .default(null)
        .required("Please confirm how long the Section 136 has been extended"),
    extensionHoursText: string().default(""),
    // Police officer approval
    policeOfficerName: nonEmptyString
        .required("Please enter the approving police officer's name")
        .nonNullable("Please enter the approving police officer's name"),
    // GMC number — required when the user is a guest and confirms they are
    // the registered medical practitioner. Full users already have verified
    // accreditations so this field is optional for them.
    // The `user` field is populated from defaultContextFields() above which
    // includes an `isGuest` boolean via the practitioner() schema (see shared.ts).
    // Uses nonEmptyString (trims + strips odd whitespace) to reject blank input.
    gmcNumber: nonEmptyString.when("user", {
        is: (user) => user?.isGuest === true,
        then: (schema) => schema
            .required("Please enter your GMC number")
            .nonNullable("Please enter your GMC number"),
    }),
})
    .test({
    name: "at-least-one-reason",
    message: "Please select at least one reason why the assessment cannot be completed within 24 hours",
    test: function (value) {
        const { reasonMentalHealth, reasonPhysicalHealth, reasonIntoxication } = value;
        if (!reasonMentalHealth && !reasonPhysicalHealth && !reasonIntoxication) {
            return this.createError({
                path: "reasonMentalHealth",
                message: "Please select at least one reason why the assessment cannot be completed within 24 hours",
            });
        }
        return true;
    },
})
    .test({
    name: "extension-within-valid-window",
    message: "Extension would exceed 36 hours from detention start",
    test: function (value) {
        const { detentionStartDateTime, extensionHoursValue } = value;
        if (!detentionStartDateTime || !extensionHoursValue) {
            return true;
        }
        const extensionHours = parseInt(extensionHoursValue, 10);
        if (isNaN(extensionHours)) {
            return true;
        }
        const startDate = dayjs(detentionStartDateTime);
        const maxEndDate = startDate.add(36, "hour");
        const proposedEndDate = startDate.add(24 + extensionHours, "hour");
        if (proposedEndDate.isAfter(maxEndDate)) {
            return this.createError({
                path: "extensionHoursValue",
                message: "The total detention period (24 hours + extension) cannot exceed 36 hours",
            });
        }
        return true;
    },
});
export const exampleS136ExtensionFormData = [
    {
        data: {
            patient: {
                name: "John Smith",
                address: "123 Test Street, London",
                postalCode: "SW1A 1AA",
                dateOfBirth: "1985-06-15",
                nhsNumber: "1234567890",
            },
            user: {
                name: "Dr Jane Doe",
                address: "456 Medical Centre, London",
                postalCode: "W1A 1AA",
                email: "jane.doe@nhs.net",
                isConfirmed: true,
                isGuest: false,
                isSection12Approved: true,
                localAuthority: "",
            },
            isRegisteredMedicalPractitioner: true,
            placeOfSafety: {
                name: "St Thomas' Hospital S136 Suite",
                address: "Westminster Bridge Road, London",
                postalCode: "SE1 7EH",
                isConfirmed: true,
                placeOfSafetyId: undefined,
                trustId: undefined,
                trustName: undefined,
            },
            placeOfSafetyTypeValue: "health-based",
            placeOfSafetyTypeText: "Health Based Place of Safety (S136 Suite)",
            reasonMentalHealth: true,
            reasonPhysicalHealth: false,
            reasonIntoxication: true,
            intoxicationTypeValue: "alcohol",
            intoxicationTypeText: "Alcohol",
            fullReasonText: "Patient presented in a highly distressed state with acute alcohol intoxication. Mental state examination cannot be reliably completed until effects of intoxication have sufficiently subsided. Patient's mental health symptoms remain concerning and require proper assessment once capacity to engage is restored.",
            detentionStartDateTime: "2024-01-15T14:30:00.000Z",
            extensionHoursValue: "6",
            extensionHoursText: "6 hours",
            policeOfficerName: "Superintendent James Wilson",
            gmcNumber: null,
        },
    },
];
//# sourceMappingURL=s136ExtensionForm.js.map